/**
 * SupplierSection.jsx — 발주처 관리 섹션
 *
 * 기능:
 *  - 발주처 목록 테이블 (이름·기본택배사·상태·수정/삭제)
 *  - 신규/수정 인라인 폼 (발주처명, 기본택배사, 활성 여부)
 *  - 행별 삭제 확인 인라인 UI
 *
 * Named export: SupplierSection
 */
import React from 'react';
import CourierInput from '../../../components/CourierInput';
import {
  SortIcon,
  cardStyle, cardHeaderStyle, cardTitleStyle,
  inlineFormStyle, fieldGroup, fieldLabel, inputStyle,
  tableStyle, thStyle, tdStyle, trStyle,
  addBtnStyle, saveBtnStyle, cancelBtnStyle, editBtnStyle,
} from './styles';
import { stdBadgeActive, stdBadgeMuted } from '../adminlayout/designStandard';

/**
 * 발주처 관리 카드 섹션
 *
 * Props:
 *   suppliers            {Array}    — 전체 발주처 목록
 *   sortedSuppliers      {Array}    — 정렬 적용된 발주처 목록
 *   supplierSort         {object}   — { key, dir } 현재 정렬 상태
 *   onSortChange         {function} — (key) 정렬 키 변경 핸들러
 *   editingSupplier      {object|null} — 편집 중인 발주처 객체
 *   setEditingSupplier   {function} — editingSupplier 상태 변경
 *   confirmDeleteId      {number|null} — 삭제 확인 대기 중인 발주처 id
 *   setConfirmDeleteId   {function} — confirmDeleteId 상태 변경
 *   saving               {boolean}  — 저장/삭제 진행 중 여부
 *   onSave               {function} — 저장 버튼 클릭 핸들러
 *   onDelete             {function} — (id) 삭제 확인 핸들러
 *   emptySupplier        {function} — 빈 발주처 객체 생성 함수
 */
export function SupplierSection({
  sortedSuppliers,
  supplierSort,
  onSortChange,
  editingSupplier,
  setEditingSupplier,
  confirmDeleteId,
  setConfirmDeleteId,
  saving,
  onSave,
  onDelete,
  emptySupplier,
}) {
  return (
    <div style={cardStyle}>
      {/* 카드 헤더: 제목 + 추가 버튼 */}
      <div style={cardHeaderStyle}>
        <span style={cardTitleStyle}>발주처 관리</span>
        <button
          style={addBtnStyle}
          onClick={() => setEditingSupplier(emptySupplier())}
        >
          + 발주처 추가
        </button>
      </div>

      {/* 신규/수정 인라인 폼 — 편집 중일 때만 표시 */}
      {editingSupplier && (
        <div style={inlineFormStyle}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {/* 발주처명 입력 */}
            <div style={fieldGroup}>
              <label style={fieldLabel}>발주처명 *</label>
              <input
                style={inputStyle}
                placeholder="발주처명"
                value={editingSupplier.name}
                onChange={(e) =>
                  setEditingSupplier((p) => ({ ...p, name: e.target.value }))
                }
              />
            </div>

            {/* 기본택배사 — CourierInput 자동완성 */}
            <div style={fieldGroup}>
              <label style={fieldLabel}>
                기본택배사{' '}
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(변경 가능)</span>
              </label>
              <CourierInput
                value={editingSupplier.default_courier}
                onChange={(v) =>
                  setEditingSupplier((p) => ({ ...p, default_courier: v }))
                }
                placeholder="예: CJ대한통운"
                style={{ minWidth: 130 }}
              />
            </div>

            {/* 활성 여부 선택 */}
            <div style={fieldGroup}>
              <label style={fieldLabel}>활성</label>
              <select
                style={{ ...inputStyle, width: 80 }}
                value={editingSupplier.is_active ? 'true' : 'false'}
                onChange={(e) =>
                  setEditingSupplier((p) => ({
                    ...p,
                    is_active: e.target.value === 'true',
                  }))
                }
              >
                <option value="true">활성</option>
                <option value="false">비활성</option>
              </select>
            </div>

            {/* 저장/취소 버튼 */}
            <div style={{ display: 'flex', gap: 6, paddingBottom: 2 }}>
              <button style={saveBtnStyle} disabled={saving} onClick={onSave}>
                {saving ? '저장중…' : '저장'}
              </button>
              <button
                style={cancelBtnStyle}
                onClick={() => setEditingSupplier(null)}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 발주처 목록 테이블 */}
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {/* 정렬 가능한 헤더 열 */}
              {[
                { label: '발주처명',   key: 'name' },
                { label: '기본택배사', key: 'default_courier' },
                { label: '상태',       key: 'is_active' },
              ].map(({ label, key }) => (
                <th
                  key={key}
                  style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortChange(key)}
                >
                  {label}
                  <SortIcon current={supplierSort} colKey={key} />
                </th>
              ))}
              {/* 수정/삭제 버튼 열 — 정렬 없음 */}
              <th style={thStyle} />
            </tr>
          </thead>
          <tbody>
            {/* 발주처가 없을 때 빈 안내 행 */}
            {sortedSuppliers.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-muted)' }}
                >
                  발주처가 없습니다.
                </td>
              </tr>
            )}

            {sortedSuppliers.map((s, idx) => {
              // 마지막 행은 밑줄 제거 (카드 테두리와 겹침 방지 — 디자인 표준)
              const rowTd = idx === sortedSuppliers.length - 1 ? { ...tdStyle, borderBottom: 'none' } : tdStyle;
              return (
              <tr key={s.id} style={trStyle}>
                {/* 발주처명 */}
                <td style={{ ...rowTd, fontWeight: 700 }}>{s.name}</td>

                {/* 기본택배사 */}
                <td style={rowTd}>
                  {s.default_courier || (
                    <span style={{ color: 'var(--border)' }}>-</span>
                  )}
                </td>

                {/* 활성/비활성 배지 — 디자인 표준 배지 */}
                <td style={rowTd}>
                  <span style={s.is_active ? stdBadgeActive : stdBadgeMuted}>
                    {s.is_active ? '활성' : '비활성'}
                  </span>
                </td>

                {/* 수정/삭제 버튼 열 */}
                <td style={{ ...rowTd, whiteSpace: 'nowrap' }}>
                  {/* 삭제 확인 인라인 UI */}
                  {confirmDeleteId === s.id ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      <span
                        style={{ fontSize: 11, color: '#dc2626', fontWeight: 700 }}
                      >
                        삭제?
                      </span>
                      <button
                        style={{
                          fontSize: 11,
                          padding: '2px 8px',
                          background: '#dc2626',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 5,
                          cursor: 'pointer',
                          fontWeight: 700,
                        }}
                        disabled={saving}
                        onClick={() => onDelete(s.id)}
                      >
                        확인
                      </button>
                      <button
                        style={{
                          fontSize: 11,
                          padding: '2px 8px',
                          background: 'var(--bg-subtle)',
                          color: 'var(--text-muted)',
                          border: 'none',
                          borderRadius: 5,
                          cursor: 'pointer',
                        }}
                        onClick={() => setConfirmDeleteId(null)}
                      >
                        취소
                      </button>
                    </span>
                  ) : (
                    /* 기본 수정/삭제 버튼 */
                    <span style={{ display: 'inline-flex', gap: 5 }}>
                      <button
                        style={editBtnStyle}
                        onClick={() => {
                          // 삭제 확인 UI 닫고 수정 폼 열기
                          setConfirmDeleteId(null);
                          setEditingSupplier({ ...s });
                        }}
                      >
                        수정
                      </button>
                      <button
                        style={{
                          fontSize: 11,
                          padding: '3px 10px',
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: 'none',
                          borderRadius: 5,
                          cursor: 'pointer',
                          fontWeight: 700,
                        }}
                        onClick={() => {
                          // 편집 폼 닫고 삭제 확인 UI 표시
                          setEditingSupplier(null);
                          setConfirmDeleteId(s.id);
                        }}
                      >
                        삭제
                      </button>
                    </span>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
